<template>
  <div class="product-selector" ref="wrapper">
    <VueSelect
      v-model="selectedProduct"
      :options="options"
      @update:model-value="handleModelValueChange"
      :isSearchable="false"
      :isClearable="false"
      :placeholder="t('productSelect.placeholder')"
      class="product-selector-control"
      @menu-opened="handleSelectOpen"
      @menu-closed="handleSelectClose"
      :default="appStore.configuration?.components.productSelect.defaultProduct"
    >
      <template #option="{ option }">
        <span
          :style="{
            fontWeight: option.value === selectedProduct ? '700' : '500',
            color:
              option.value === selectedProduct ? 'var(--menu-item-color)' : 'var(--text-color)',
          }"
        >
          {{ option.label }}
        </span>
      </template>
    </VueSelect>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import VueSelect from 'vue3-select-component'
import { t } from './../composables/Translation'
import { useAppStore } from '../stores/appStore'
import { getDefaultProduct, setProduct } from '../composables/ProductSelector'

const appStore = useAppStore()

const options = ref<{ label: string; value: string }[]>([])

const selectedProduct = ref(getDefaultProduct())
const wrapper = ref<HTMLElement | null>(null)
const isMenuOpened = ref(false)

watch(
  () => appStore.configuration?.components.productSelect,
  async productSelect => {
    if (productSelect) {
      options.value = productSelect.availableProducts.map((p: string) => ({
        label: p,
        value: p,
      }))

      selectedProduct.value = getDefaultProduct()
    }
  },
  { immediate: true }
)

function handleModelValueChange(value: string | string[]) {
  const selectedValue = Array.isArray(value) ? (value[0] ?? null) : value
  setProduct(selectedValue)
  console.log('Product changed to:', selectedValue)
}

watch(
  () => appStore.productFromUrl,
  newProduct => {
    if (!newProduct) return
    // only update if it's a valid available option
    const isValid = options.value.some(o => o.value === newProduct)
    if (isValid && selectedProduct.value !== newProduct) {
      console.log('Updating selectedProduct from appStore.productInUrl:', newProduct)
      selectedProduct.value = newProduct
    }
  },
  { immediate: true }
)

watch(
  () => appStore.selectedProduct,
  newSelectedProduct => {
    selectedProduct.value = newSelectedProduct ?? ''
  }
)

async function handleSelectOpen() {
  await nextTick()
  if (!isMenuOpened.value && wrapper.value) {
    const dropdown = wrapper.value.querySelector('.product-select .menu')
    if (dropdown) {
      const rect = dropdown.getBoundingClientRect()
      if (rect.right > window.innerWidth) {
        dropdown.setAttribute('style', 'left:auto!important;right:0!important;')
      } else {
        dropdown.removeAttribute('style')
      }
    }
    isMenuOpened.value = true
  }
}

function handleSelectClose() {
  isMenuOpened.value = false
}
</script>

<style scoped lang="scss">
.product-selector {
  cursor: pointer;

  /* Product select base control variables */
  .product-selector-control {
    --vs-font-weight: 500;
    --vs-option-padding: 8px;
    --vs-option-font-size: 14px;
    --vs-option-font-weight: 500;
    --vs-option-opacity-menu-open: 1;
    --vs-option-hover-background-color: var(--text-background-color);
    --vs-option-focused-background-color: var(--text-background-color);
    --vs-option-selected-background-color: var(--text-background-color);
    --vs-text-color: var(--text-color);
    --vs-indicator-icon-color: var(--text-color);
  }

  :deep(.vue-select .input-container input) {
    display: none !important;
  }

  :deep(.vue-select .menu) {
    min-width: 155px !important;
  }

  :deep(.vue-select),
  :deep(.vue-select .control),
  :deep(.vue-select .control.focused) {
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    outline: none !important;
  }

  :deep(.vue-select .value-container) {
    font-weight: 500;
  }

  :deep(.vue-select .dropdown-icon) {
    width: 16px;
    height: 16px;
    margin-left: 4px;
  }

  :deep(.vue-select .menu-option) {
    font-weight: 500;
    color: var(--text-color);
  }
}
</style>
